import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FaArrowRight,
  FaExclamationTriangle,
  FaFilter,
  FaPlane,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";
import FlightCard from "../helpers/FlightCard";

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatCabinClass = (value = "economy") =>
  value.replace("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());

const getDurationMinutes = (duration = "") => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return 0;
  return Number(match[1] || 0) * 60 + Number(match[2] || 0);
};

const getOfferDuration = (offer) =>
  (offer.slices || []).reduce(
    (total, slice) => total + getDurationMinutes(slice.duration),
    0,
  );

const parseAmount = (value) => {
  const amount = Number.parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const getOfferTotal = (offer) =>
  parseAmount(offer.total_amount ?? offer.intended_total_amount);

const FilterPanel = ({ directOnly, onClose, isMobile }) => (
  <div className="flex h-full flex-col rounded-none border-slate-200 bg-white p-6 lg:sticky lg:top-24 lg:h-auto lg:rounded-[1.5rem] lg:border lg:shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
    <div className="mb-6 flex items-center justify-between border-b border-slate-100 pb-5">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#2871d3]">
          Refine
        </p>
        <h2 className="mt-1 text-xl font-black text-slate-950">Filters</h2>
      </div>
      {isMobile && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close filters"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
        >
          <FaTimes />
        </button>
      )}
    </div>

    <div className="space-y-7">
      <div>
        <h3 className="mb-3 text-sm font-black text-slate-950">Stops</h3>
        <div className="space-y-3">
          {["Direct (0 Stops)", "1 Stop", "2+ Stops"].map((stop, index) => (
            <label
              key={stop}
              className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-slate-600 transition hover:text-slate-950"
            >
              <input
                type="checkbox"
                defaultChecked={index === 0 ? directOnly : false}
                className="h-5 w-5 rounded border-slate-300 accent-[#2871d3]"
              />
              {stop}
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-100 pt-6">
        <h3 className="mb-3 text-sm font-black text-slate-950">Airlines</h3>
        <div className="space-y-3">
          {["Emirates", "Air India", "Qatar Airways", "Etihad"].map(
            (airline) => (
              <label
                key={airline}
                className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-slate-600 transition hover:text-slate-950"
              >
                <input
                  type="checkbox"
                  defaultChecked
                  className="h-5 w-5 rounded border-slate-300 accent-[#2871d3]"
                />
                {airline}
              </label>
            ),
          )}
        </div>
      </div>

      <div className="border-t border-slate-100 pt-6">
        <h3 className="mb-4 text-sm font-black text-slate-950">Price Range</h3>
        <input
          type="range"
          className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-[#2871d3]"
        />
        <div className="mt-2 flex justify-between text-xs font-bold text-slate-400">
          <span>Lowest</span>
          <span>Highest</span>
        </div>
      </div>
    </div>

    {isMobile && (
      <button
        type="button"
        onClick={onClose}
        className="mt-auto rounded-full bg-[#2871d3] px-6 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-600/20"
      >
        Show Results
      </button>
    )}
  </div>
);

const FlightResultsPage = () => {
  const [searchParams] = useSearchParams();

  const originIata = searchParams.get("origin");
  const originName = searchParams.get("originName") || originIata;
  const destIata = searchParams.get("dest");
  const destName = searchParams.get("destName") || destIata;
  const departDate = searchParams.get("departDate");
  const returnDate = searchParams.get("returnDate");
  const tripType = searchParams.get("tripType");
  const cabinClass = searchParams.get("cabinClass") || "economy";
  const directOnly = searchParams.get("directOnly") === "true";

  const [flightOffers, setFlightOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [activeSort, setActiveSort] = useState("Cheapest");

  useEffect(() => {
    document.body.style.overflow = showMobileFilters ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showMobileFilters]);

  useEffect(() => {
    const fetchFlights = async () => {
      setLoading(true);
      setError(null);
      setFlightOffers([]);

      const slices = [
        {
          origin: originIata,
          destination: destIata,
          departure_date: departDate,
        },
      ];

      if (tripType === "return" && returnDate) {
        slices.push({
          origin: destIata,
          destination: originIata,
          departure_date: returnDate,
        });
      }

      try {
        const response = await fetch(
          "https://cheapoflyhub-api.mohitbajaj1995.workers.dev/flights/search",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              data: {
                slices,
                passengers: [{ type: "adult" }],
                cabin_class: cabinClass,
                directOnly,
              },
            }),
          },
        );

        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
        const result = await response.json();

        if (result.data?.offers) {
          setFlightOffers(result.data.offers);
          if (result.data.offers.length === 0) {
            setError("No flights found for this route and date.");
          }
        } else {
          setError("Unexpected response format from server.");
        }
      } catch (err) {
        setError(
          "An error occurred while searching for flights. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (originIata && destIata && departDate) {
      fetchFlights();
    } else {
      setError("Missing search parameters. Please go back and search again.");
      setLoading(false);
    }
  }, [
    originIata,
    destIata,
    departDate,
    returnDate,
    tripType,
    cabinClass,
    directOnly,
  ]);

  const sortedOffers = useMemo(() => {
    const offers = [...flightOffers];
    if (activeSort === "Fastest") {
      return offers.sort((a, b) => getOfferDuration(a) - getOfferDuration(b));
    }
    if (activeSort === "Best") {
      return offers.sort(
        (a, b) =>
          getOfferTotal(a) +
          getOfferDuration(a) * 0.35 -
          (getOfferTotal(b) + getOfferDuration(b) * 0.35),
      );
    }
    return offers.sort((a, b) => getOfferTotal(a) - getOfferTotal(b));
  }, [activeSort, flightOffers]);

  const routeDate = `${formatDate(departDate)}${
    tripType === "return" && returnDate ? ` - ${formatDate(returnDate)}` : ""
  }`;

  return (
    <div className="min-h-screen bg-[#f8f9fc] pb-20 font-sans lg:pb-16">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="mb-3 inline-flex rounded-full bg-blue-50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#2871d3]">
                Flight results
              </p>
              <h1 className="flex flex-wrap items-center gap-3 text-2xl font-black tracking-tight text-slate-950 sm:text-4xl">
                <span>{originName}</span>
                <FaArrowRight className="h-5 w-5 text-[#2871d3]" />
                <span>{destName}</span>
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-500">
                <span className="rounded-full bg-[#f8f9fc] px-3 py-1">
                  {routeDate}
                </span>
                <span className="rounded-full bg-[#f8f9fc] px-3 py-1">
                  1 Adult
                </span>
                <span className="rounded-full bg-[#f8f9fc] px-3 py-1">
                  {formatCabinClass(cabinClass)}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => window.history.back()}
              className="rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-black text-slate-950 shadow-sm transition hover:border-blue-200 hover:text-[#2871d3]"
            >
              Modify Search
            </button>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:px-8">
        <aside className="hidden lg:block">
          <FilterPanel directOnly={directOnly} />
        </aside>

        <main className="min-w-0">
          {loading && (
            <div className="flex min-h-[360px] flex-col items-center justify-center rounded-[1.5rem] border border-slate-200 bg-white p-10 text-center shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
              <FaSpinner className="mb-6 h-12 w-12 animate-spin text-[#2871d3]" />
              <h2 className="text-2xl font-black text-slate-950">
                Searching hundreds of flights...
              </h2>
              <p className="mt-2 max-w-md text-sm font-medium leading-6 text-slate-500">
                We are checking available fares and route options for your trip.
              </p>
            </div>
          )}

          {error && (
            <div className="flex min-h-[320px] flex-col items-center justify-center rounded-[1.5rem] border border-red-100 bg-red-50 p-10 text-center shadow-sm">
              <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-700">
                <FaExclamationTriangle className="h-7 w-7" />
              </div>
              <h2 className="text-2xl font-black text-red-950">
                Search unfulfilled
              </h2>
              <p className="mt-2 max-w-md text-sm font-semibold leading-6 text-red-700">
                {error}
              </p>
              <button
                type="button"
                onClick={() => window.history.back()}
                className="mt-6 rounded-full bg-red-800 px-6 py-3 text-sm font-black text-white shadow-lg shadow-red-900/20 transition hover:bg-red-900"
              >
                Go Back & Modify
              </button>
            </div>
          )}

          {!loading && !error && sortedOffers.length > 0 && (
            <>
              <div className="sticky top-[78px] z-30 mb-5 rounded-[1.25rem] border border-slate-200 bg-white/95 p-3 shadow-[0_12px_35px_rgba(15,23,42,0.08)] backdrop-blur-xl">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center justify-between gap-3 px-1">
                    <div className="flex items-center gap-3 text-sm font-bold text-slate-600">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-[#2871d3]">
                        <FaPlane />
                      </span>
                      <span>
                        Showing{" "}
                        <strong className="text-slate-950">
                          {sortedOffers.length}
                        </strong>{" "}
                        results
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowMobileFilters(true)}
                      className="flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-black text-[#2871d3] lg:hidden"
                    >
                      <FaFilter className="h-3.5 w-3.5" />
                      Filters
                    </button>
                  </div>

                  <div className="flex overflow-x-auto rounded-full bg-[#f8f9fc] p-1">
                    {["Cheapest", "Fastest", "Best"].map((sort) => (
                      <button
                        key={sort}
                        type="button"
                        onClick={() => setActiveSort(sort)}
                        className={`min-h-10 rounded-full px-5 text-sm font-black transition-all ${
                          activeSort === sort
                            ? "bg-[#2871d3] text-white shadow-sm shadow-blue-600/20"
                            : "text-slate-500 hover:text-slate-950"
                        }`}
                      >
                        {sort}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                {sortedOffers.map((offer) => (
                  <FlightCard key={offer.id} offer={offer} />
                ))}
              </div>
            </>
          )}
        </main>
      </div>

      <div
        className={`fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-sm transition lg:hidden ${
          showMobileFilters
            ? "visible opacity-100"
            : "invisible opacity-0"
        }`}
        onClick={() => setShowMobileFilters(false)}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[86%] max-w-sm bg-white shadow-2xl transition-transform duration-300 lg:hidden ${
          showMobileFilters ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <FilterPanel
          directOnly={directOnly}
          isMobile
          onClose={() => setShowMobileFilters(false)}
        />
      </aside>
    </div>
  );
};

export default FlightResultsPage;
